import { ApolloError } from '@apollo/client';
import type { ServerParseError } from '@apollo/client/link/http';
import type { ServerError } from '@apollo/client/link/utils';
import isNil from 'lodash/fp/isNil';

export const DEFAULT_MESSAGE = 'We’re already looking into it. Please try again later.';

const TIMEOUT_CODE = 'timeout';

const ERROR_MESSAGES: Record<string, string> = {
  // GraphQL codes
  account_invalid_api_keys: 'Incorrect API keys',
  sub_account_duplicate_name: 'Sub-account with the same name already exists',
  account_duplicate_name: 'Account with the same name already exists',
  portfolio_service_unavailable: 'Portfolio service unavailable, try again later',
  'BENCHMARK NOT FOUND': 'Benchmark not found',
  // Shared codes
  asset_duplicate_name: 'An asset with this name already exists.',
  [TIMEOUT_CODE]: 'Timeout occurred. Please try again later.',
};

const isServerError = (
  error: Error | ServerParseError | ServerError | null
): error is ServerError | ServerParseError => {
  // @ts-ignore
  return !isNil(error?.statusCode);
};

export const getErrorDetails = (
  exception: unknown
): { code?: string; message?: string | string[]; path?: string[] } | undefined => {
  if (!(exception instanceof ApolloError)) return undefined;

  if (isServerError(exception.networkError) && exception.networkError.statusCode === 504) {
    return {
      code: TIMEOUT_CODE,
      message: ERROR_MESSAGES[TIMEOUT_CODE],
    };
  }

  const firstError = exception.graphQLErrors?.[0];
  if (!firstError) return undefined;

  const extensionsMessage = firstError.extensions?.message as string | string[] | undefined;
  const message = extensionsMessage ?? undefined;

  const code = firstError.extensions?.code as string | undefined;
  const path = firstError.path as string[] | undefined;

  return { code, message, path };
};

export const getFriendlyErrorMessage = ({
  code,
  message,
}: {
  code?: string;
  message?: string | string[];
}): string => {
  if (code && ERROR_MESSAGES[code]) {
    return ERROR_MESSAGES[code];
  }

  const messageStr = typeof message === 'string' ? message : Array.isArray(message) ? message.join(', ') : '';

  return messageStr || DEFAULT_MESSAGE;
};

export const calculateErrorMessage = (exception: unknown): string => {
  const details = getErrorDetails(exception);
  if (!details) return DEFAULT_MESSAGE;

  return getFriendlyErrorMessage(details);
};

export const extractUserFacingMessage = (exception: unknown): string | string[] => {
  const details = getErrorDetails(exception);
  if (details?.message) return details.message;

  return DEFAULT_MESSAGE;
};
